/**
 * AIConsentContext — global gate that satisfies Apple 5.1.1(i) & 5.1.2(i).
 *
 * Exposes `requireConsent()` — a promise-returning helper that resolves
 * `true` if the user has already consented (or successfully consents now),
 * and `false` if they explicitly decline. Any AI entry point (chat, OCR,
 * voice transcription, document rewrite) is expected to `await` this
 * before triggering an AI call. The provider mounts a single global
 * <AIConsentGate/> modal so the UX is uniform everywhere.
 *
 * We also expose `status`, `refresh`, and `withdraw` so pages like
 * Settings can build a toggle without duplicating fetch logic.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { getUserToken } from "../lib/userApi";
import {
  getAIConsent,
  grantAIConsent,
  withdrawAIConsent,
} from "../lib/aiConsentApi";
import AIConsentGate from "../components/legal/AIConsentGate";

const AIConsentContext = createContext({
  status: null,
  loading: true,
  requireConsent: async () => false,
  refresh: async () => null,
  withdraw: async () => null,
});

export function AIConsentProvider({ children }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gateOpen, setGateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // The pending decision resolver — populated when a caller awaits
  // `requireConsent()` and drained when the user picks Accept/Deny.
  const pendingResolveRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!getUserToken()) {
      setStatus(null);
      setLoading(false);
      return null;
    }
    try {
      const s = await getAIConsent();
      setStatus(s);
      return s;
    } catch {
      setStatus(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const settleDecision = useCallback((value) => {
    if (pendingResolveRef.current) {
      pendingResolveRef.current(value);
      pendingResolveRef.current = null;
    }
    setGateOpen(false);
  }, []);

  const handleAccept = useCallback(async () => {
    setSubmitting(true);
    try {
      const res = await grantAIConsent();
      setStatus((prev) => ({
        ...(prev || {}),
        granted: true,
        version: res.version,
        granted_at: res.granted_at,
        withdrawn_at: null,
      }));
      settleDecision(true);
    } catch (e) {
      toast.error("تَعَذَّر حِفظ المُوافَقة. حاوِل مَرَّة أُخرى.");
    } finally {
      setSubmitting(false);
    }
  }, [settleDecision]);

  const handleDeny = useCallback(() => {
    if (submitting) return;
    settleDecision(false);
  }, [settleDecision, submitting]);

  const requireConsent = useCallback(async () => {
    // Fast path — cached status says granted.
    if (status?.granted) return true;
    // If we don't yet know, do a synchronous fetch first before opening
    // the modal so we never flash it to a user who consented on another
    // device seconds ago.
    if (!status) {
      const s = await refresh();
      if (s?.granted) return true;
    }
    return new Promise((resolve) => {
      pendingResolveRef.current = resolve;
      setGateOpen(true);
    });
  }, [status, refresh]);

  const withdraw = useCallback(async () => {
    try {
      const res = await withdrawAIConsent();
      setStatus((prev) => ({
        ...(prev || {}),
        granted: false,
        version: res.version,
        withdrawn_at: res.withdrawn_at,
        granted_at: null,
      }));
      return res;
    } catch (e) {
      toast.error("تَعَذَّر سَحب المُوافَقة. حاوِل مَرَّة أُخرى.");
      return null;
    }
  }, []);

  return (
    <AIConsentContext.Provider
      value={{ status, loading, requireConsent, refresh, withdraw }}
    >
      {children}
      <AIConsentGate
        open={gateOpen}
        submitting={submitting}
        onAccept={handleAccept}
        onDeny={handleDeny}
      />
    </AIConsentContext.Provider>
  );
}

export const useAIConsent = () => useContext(AIConsentContext);
